import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  ListItemButton,
  Modal,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { RiCloseFill, RiEditBoxLine } from "react-icons/ri";
import { FiImage } from "react-icons/fi";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  DELETE_CATEGORIES,
  DELETE_SUB_CATEGORY,
  EDIT_CATEGORY,
  EDIT_SUB_CATEGORY,
} from "../../../Utils/Mutation";
import { useSnackbarHook } from "../../../hooks/useSnackbarHook";
import deleteIcon from "../../../assets/img/delete.svg";
import Loader from "../../Common/Loader";
import { GET_SUB_CATEGORIES } from "../../../Utils/Queries";
import { CloseOutlined } from "@mui/icons-material";
import CustomInput from "../../Common/CustomInput";
import { uploadSingleImageToCloudinary } from "../../../Utils/productEditHelpers";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 540,
    flexShrink: 0,
    p: 4,
  },
  drawerPaper: {
    width: 540,
    p: 4,
  },
  toolbar: theme.mixins.toolbar,
}));

const initialFormState = {
  name: "",
  description: "",
  isActive: true,
  imageUrl: "",
};

const EditCategory = ({ open, onClose, data, action }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const createdPreviewUrlRef = useRef(null);

  const [editSub, setEditSub] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openModal2, setOpenModal2] = useState(false);
  const [openModal3, setOpenModal3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subList, setSubList] = useState(null);
  const [subName, setSubName] = useState("");
  const [formState, setFormState] = useState(initialFormState);
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState("");

  const { handleClick } = useSnackbarHook();

  const [deleteCategory, { loading: deleteCategoryLoading }] =
    useMutation(DELETE_CATEGORIES);
  const [deleteSubCategory] = useMutation(DELETE_SUB_CATEGORY);
  const [editSubCategory] = useMutation(EDIT_SUB_CATEGORY);
  const [editCategory, { loading: editCategoryLoading }] =
    useMutation(EDIT_CATEGORY);

  const [getSubCategory] = useLazyQuery(GET_SUB_CATEGORIES);

  useEffect(() => {
    if (!data) return;

    setFormState({
      name: data?.name || "",
      description: data?.description || "",
      isActive: Boolean(data?.isActive),
      imageUrl: data?.image || "",
    });
    setPreviewUrl(data?.image || "");
    setSelectedFile(null);
    setFormError("");
    setSubList(null);
    setSubName("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [data]);

  useEffect(() => {
    if (editSub) {
      setSubName(editSub?.name || "");
    }
  }, [editSub]);

  useEffect(() => {
    if (open && data?._id) {
      handleGetSubCategory(data._id);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      if (createdPreviewUrlRef.current) {
        URL.revokeObjectURL(createdPreviewUrlRef.current);
      }
    };
  }, []);

  const isSubmitDisabled = useMemo(
    () =>
      isUploading ||
      editCategoryLoading ||
      !formState.name.trim() ||
      !formState.imageUrl,
    [editCategoryLoading, formState.imageUrl, formState.name, isUploading],
  );

  const updateFormField = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGetSubCategory = async (id) => {
    await getSubCategory({
      variables: {
        category: id,
      },
    })
      .then((res) => {
        setSubList(res?.data?.getSubCategories || []);
      })
      .catch(() => {
        setSubList([]);
      });
  };

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFormError("Please select a valid image file.");
      handleClick("Please select a valid image file.", "error");
      return;
    }

    setFormError("");
    setSelectedFile(file);

    if (createdPreviewUrlRef.current) {
      URL.revokeObjectURL(createdPreviewUrlRef.current);
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    createdPreviewUrlRef.current = nextPreviewUrl;
    setPreviewUrl(nextPreviewUrl);

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadSingleImageToCloudinary(file);
      updateFormField("imageUrl", uploadedUrl);
      handleClick("Image uploaded successfully", "success");
    } catch (err) {
      setFormError(err?.message || "Image upload failed.");
      updateFormField("imageUrl", data?.image || "");
      setPreviewUrl(data?.image || "");
      handleClick(err?.message || "Image upload failed.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveCategory = async () => {
    setFormError("");

    if (!formState.name.trim()) {
      setFormError("Category name is required.");
      return;
    }

    if (!formState.imageUrl) {
      setFormError("Please upload a category image before saving.");
      return;
    }

    try {
      await editCategory({
        variables: {
          categoryId: data?._id,
          name: formState.name.trim(),
          description: formState.description.trim() || null,
          isActive: formState.isActive,
          isForService: Boolean(data?.isForService),
          imageUrl: formState.imageUrl,
        },
      });

      handleClick("Category updated successfully", "success");
      action?.();
      onClose();
    } catch (err) {
      const message = err?.message || "Failed to update category.";
      setFormError(message);
      handleClick(message, "error");
    }
  };

  const handleDeleteCategories = async () => {
    setIsLoading(true);
    await deleteCategory({
      variables: {
        categoryId: data?._id,
      },
    })
      .then(() => {
        setIsLoading(false);
        setOpenModal2(false);
        onClose();
        action?.();
        handleClick("Category Deleted Successfully", "success");
      })
      .catch((err) => {
        setIsLoading(false);
        handleClick(`${err.message}`, "error");
      });
  };

  const handleEditSubCategory = async () => {
    setIsLoading(true);
    await editSubCategory({
      variables: {
        subCategoryId: editSub?._id,
        name: subName,
      },
    })
      .then(() => {
        setIsLoading(false);
        handleGetSubCategory(data?._id);
        setOpenModal3(false);
        handleClick("Sub Category Edited Successfully", "success");
      })
      .catch((err) => {
        setIsLoading(false);
        handleClick(`${err.message}`, "error");
      });
  };

  const handleDeleteSubCategories = async () => {
    setIsLoading(true);
    await deleteSubCategory({
      variables: {
        subCategoryId: editSub?._id,
      },
    })
      .then(() => {
        setIsLoading(false);
        setOpenModal(false);
        handleGetSubCategory(data?._id);
        handleClick("Sub Category Deleted Successfully", "success");
      })
      .catch((err) => {
        setIsLoading(false);
        handleClick(`${err.message}`, "error");
      });
  };

  return (
    <>
      {(isLoading ||
        deleteCategoryLoading ||
        editCategoryLoading ||
        isUploading) && <Loader />}
      <Drawer
        className={classes.drawer}
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
      >
        <Box sx={{ px: 4, py: 5 }} className={classes.toolbar}>
          <Box className="flex justify-between gap-x-6">
            <div>
              <Typography
                sx={{
                  fontFamily: "Lato",
                  color: "#0D0D0D",
                  fontSize: "18px",
                  fontWeight: 900,
                }}
              >
                Edit Category
              </Typography>
              <Typography sx={{ fontSize: "12px" }}>
                Update the category details, visibility, and image while keeping
                the linked sub categories below.
              </Typography>
            </div>
            <IconButton onClick={onClose}>
              <IoMdClose style={{ fontSize: "25px", color: "#000" }} />
            </IconButton>
          </Box>

          <Box sx={{ mt: 4, display: "grid", rowGap: 3 }}>
            <TextField
              label="Category Name *"
              fullWidth
              value={formState.name}
              onChange={(e) => updateFormField("name", e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={4}
              value={formState.description}
              onChange={(e) => updateFormField("description", e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formState.isActive}
                  onChange={(e) => updateFormField("isActive", e.target.checked)}
                />
              }
              label="Active category"
            />

            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Category Image *
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handleFileSelection}
              />
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: "1px dashed #C9C9C9",
                  borderRadius: "16px",
                  p: 3,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  rowGap: 1,
                  bgcolor: "#FAFAFA",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#7B408C",
                    bgcolor: "#F8F3FA",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "#F3EAF6",
                  }}
                >
                  {isUploading ? (
                    <CircularProgress size={22} sx={{ color: "#7B408C" }} />
                  ) : (
                    <FiImage style={{ color: "#7B408C", fontSize: 20 }} />
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                  {isUploading ? "Uploading image..." : "Click to upload image"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  PNG, JPG, or WEBP
                </Typography>
              </Box>

              {(previewUrl || formState.imageUrl) && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 1 }}>
                    Preview
                  </Typography>
                  <Box
                    component="img"
                    src={previewUrl || formState.imageUrl}
                    alt={
                      selectedFile?.name || formState.name || "Category preview"
                    }
                    sx={{
                      width: "100%",
                      maxHeight: 220,
                      objectFit: "cover",
                      borderRadius: "12px",
                      border: "1px solid #E5E5E5",
                    }}
                  />
                </Box>
              )}
            </Box>

            {formError && (
              <Typography sx={{ color: "#D32F2F", fontSize: "13px" }}>
                {formError}
              </Typography>
            )}

            <Box
              sx={{
                mt: 1,
                bgcolor: "#F7F6F6",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography>Sub Categories</Typography>
              <Button sx={{ color: "#0AB110" }}>Add Sub Category</Button>
            </Box>

            <Box sx={{ mt: -1 }}>
              {!subList ? (
                <>
                  {[1, 2, 3].map((item) => (
                    <ListItemButton key={item}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Skeleton
                          variant="text"
                          width="70%"
                          sx={{ borderRadius: 0 }}
                        />
                        <Box>
                          <IconButton disabled>
                            <RiEditBoxLine />
                          </IconButton>
                          <IconButton disabled>
                            <RiCloseFill style={{ color: "#FF4C5E" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItemButton>
                  ))}
                </>
              ) : (
                <>
                  {subList.map((item, index) => (
                    <ListItemButton key={index}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#4D4D4D",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          {item?.name}
                        </Typography>
                        <Box>
                          <IconButton
                            onClick={() => {
                              setOpenModal3(true);
                              setEditSub(item);
                            }}
                          >
                            <RiEditBoxLine />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setOpenModal(true);
                              setEditSub(item);
                            }}
                          >
                            <RiCloseFill style={{ color: "#FF4C5E" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItemButton>
                  ))}
                </>
              )}
            </Box>

            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 1 }}
              onClick={handleSaveCategory}
              disabled={isSubmitDisabled}
            >
              {isUploading
                ? "Uploading Image..."
                : editCategoryLoading
                  ? "Saving..."
                  : "Save Changes"}
            </Button>
            <Button
              onClick={() => setOpenModal2(true)}
              color="error"
              variant="contained"
              fullWidth
              sx={{ mt: 1 }}
            >
              Delete-
              <span style={{ fontWeight: 700 }}>{" " + data?.name}</span>
            </Button>
          </Box>
        </Box>

        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box sx={{ display: "flex", columnGap: 3, alignItems: "center" }}>
              <img src={deleteIcon} width={200} alt="delete icon" />
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Are you sure you want to delete this sub category?
                </Typography>
                <Typography sx={{ fontSize: "12px", mt: 1 }}>
                  This sub category will be deleted immediately, you can’t undo
                  this action.
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setOpenModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteSubCategories}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Modal>

        <Modal
          open={openModal2}
          onClose={() => setOpenModal2(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box sx={{ display: "flex", columnGap: 3, alignItems: "center" }}>
              <img src={deleteIcon} width={200} alt="delete icon" />
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Are you sure you want to delete this category?
                </Typography>
                <Typography sx={{ fontSize: "12px", mt: 1 }}>
                  This category will be deleted immediately, you can’t undo this
                  action.
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setOpenModal2(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteCategories}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Modal>

        <Modal
          open={openModal3}
          onClose={() => setOpenModal3(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                Edit
              </Typography>
              <IconButton onClick={() => setOpenModal3(false)}>
                <CloseOutlined sx={{ fontSize: "20px" }} />
              </IconButton>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 400 }}>
                Sub Category Title
              </Typography>
              <CustomInput
                value={subName}
                onChange={(e) => setSubName(e.target.value)}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleEditSubCategory}
                  sx={{ borderRadius: "10px" }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Drawer>
    </>
  );
};

export default EditCategory;
